import React, { useState, useEffect, useCallback, useContext } from "react";
import { Dropdown, Badge, Typography, Image, Button, Space } from "antd";
import {
  BellOutlined,
  MailOutlined,
  CheckCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
  ReadOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import ThemeContext from "../../utils/context/Theme/ThemeContext";
import { Notifications } from "../../apis/notifications";
import "./NotificationDashList.css";

const NotificationDashList = () => {
  const { Text } = Typography;
  const { themeMode } = useContext(ThemeContext);

  const { t } = useTranslation("placeholders");

  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);
  const [isNoti, setIsNoti] = useState(true);

  // Icon Mapping
  const iconMap = {
    "heroicon-o-envelope": <MailOutlined />,
    "heroicon-o-check-circle": <CheckCircleOutlined />,
  };

  const notificationTypeMap = {
    SupportMessageNotification: {
      route: "/support",
    },
  };

const fetchNotifications = useCallback(
  async (signal) => {
    setLoading(true);

    try {
      const data = await Notifications.getNotifications({}, signal);

      if (signal?.aborted) return;

      setNotifications(data);
      setError(null);
    } catch (err) {
      if (signal?.aborted) return;

      setNotifications([]);
      setError(t("notifications.failed"));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  },
  [t],
);

  useEffect(() => {
    const controller = new AbortController();
    fetchNotifications(controller.signal);

    return () => controller.abort();
  }, [fetchNotifications]);

  useEffect(() => {
    setIsNoti(notifications.some((n) => !n.read_at));
  }, [notifications]);

  const markAsRead = async (notification, action = "read") => {
    try {
      await Notifications.markAsRead(notification.id, action);
      fetchNotifications();
    } catch {
      console.error("Error marking notification as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await Notifications.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch {
      console.error("Error deleting notification");
    }
  };

  const menu = (notification) => [
    {
      key: "mark",
      icon: <ReadOutlined />,
      label: (
        <span
          onClick={() =>
            markAsRead(notification, notification.read_at ? "unread" : "read")
          }
        >
          {notification.read_at
            ? t("notifications.unread")
            : t("notifications.read")}
        </span>
      ),
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: (
        <span onClick={() => deleteNotification(notification.id)}>
          {t("notifications.delete")}
        </span>
      ),
    },
  ];

  return (
    <Dropdown
      overlayClassName={classNames("noti-dropdown", {
        darkDropDown: themeMode === "dark",
        lightDropDown: themeMode === "light",
        "multiple-notifications": notifications.length > 2 && !error,
      })}
      menu={{
        items: [
          {
            key: "notifications",
            label: (
              <div className="pb-15" onClick={(e) => e.stopPropagation()}>
                <Space className="noti-header w-100" align="center" wrap>
                  <Text strong>{t("notifications.label")}</Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const controller = new AbortController();
                      fetchNotifications(controller.signal);
                    }}
                  >
                    {t("refresh")}
                  </Button>
                </Space>
                {loading ? (
                  <Text className="text"> {t("loading")}...</Text>
                ) : error ? (
                  <Text className="text">{error}</Text>
                ) : notifications.length > 0 ? (
                  notifications.map((item) => {
                    const { image, icon, read_at, title, body, since } = item;
                    const notiIcon = iconMap[icon] || (
                      <Image
                        preview={false}
                        src={image || ""}
                        alt="Notification"
                        className="noti-image"
                      />
                    );

                    return (
                      <Space
                        size={12}
                        align="start"
                        key={item.id}
                        className={`noti-item relative ${
                          read_at ? "read" : "unread"
                        }`}
                      >
                        {notiIcon}
                        {!read_at && (
                          <span className="unread-indicator absolute"></span>
                        )}
                        <Space
                          direction="vertical"
                          className="noti-content text-start"
                        >
                          <Text strong={!read_at}>{title}</Text>
                          <Text className="description">{body}</Text>
                          <small>{since}</small>
                        </Space>
                        <Dropdown
                          menu={{ items: menu(item) }}
                          overlayClassName={classNames("more-menu", {
                            darkDropDown: themeMode === "dark",
                          })}
                          trigger={["click"]}
                        >
                          <MoreOutlined className="noti-actions" />
                        </Dropdown>
                      </Space>
                    );
                  })
                ) : (
                  <Text className="text">{t("notifications.nothing")}</Text>
                )}
              </div>
            ),
          },
        ],
      }}
      trigger={["click"]}
    >
      <Badge dot={isNoti}>
        <BellOutlined />
      </Badge>
    </Dropdown>
  );
};

export default NotificationDashList;
